import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import usePageTitle from '../../hooks/usePageTitle';
import { money } from './caseBuilderModel';
import './caseBuilder.css';

const STAGES = [
  ['snapshot', 'Saved case'],
  ['references', 'Reference sources'],
  ['validate', 'Check the claim'],
  ['inventory', 'List the denials'],
  ['activate', 'Choose the checks'],
  ['execute', 'Read the evidence'],
  ['lines', 'Decide each line'],
  ['conflicts', 'Check for conflicts'],
  ['gates', 'Apply the checks'],
  ['estimate', 'Estimate recovery'],
  ['remedies', 'Note what was not filed'],
  ['revision', 'Compare with the last run'],
];

const WORDS = {
  RECONSIDERATION: 'Reconsideration',
  CORRECT_CLAIM: 'Corrected claim',
  HOLD_FOR_EVIDENCE: 'Needs more information',
  VOID_OR_WITHDRAW_PROPOSAL: 'Withdraw this line',
  NO_ACTION: 'Already paid',
  PROVIDE_INFORMATION: 'Send missing information',
  ACCEPT_DENIAL: 'Accept the denial',
  CONTRACT_DISPUTE: 'Contract dispute',
  APPEAL: 'Appeal',
  HUMAN_REVIEW: 'Needs a person to review',
  NOT_EVALUATED: 'Not evaluated',
  SEE_LINE_DISPOSITIONS: 'See each line',
  SUPPORTED: 'Supported',
  NOT_SUPPORTED: 'Not supported',
  UNKNOWN: 'Not enough information',
  CONTRADICTED: 'Contradicted',
  PASS: 'Passed',
  REVIEW: 'Needs review',
  BLOCK: 'Blocked',
  NOT_APPLICABLE: 'Does not apply',
  COMPLETED: 'Finished',
  NEEDS_INFORMATION: 'Needs more information',
  BLOCKED: 'Blocked',
  AWAITING_HUMAN_REVIEW: 'Waiting for a person to review',
  MEDICAL_NECESSITY: 'Medical necessity',
  SERVICE_PERFORMED: 'Service performed',
  CLINICAL_DOCUMENTATION: 'Chart documentation',
  PAYER_POLICY: 'Payer policy',
  AUTHORIZATION: 'Authorization',
  EMERGENCY_EXCEPTION: 'Emergency exception',
  PLACE_OF_SERVICE: 'Place of service',
  BUNDLING: 'Bundling',
  CONTRACT_EXCEPTION: 'Contract exception',
  MISSING_INFORMATION: 'Missing information',
  PROVIDER_IDENTIFIER: 'Provider identifier',
  CORRECTED_CLAIM: 'Corrected claim',
  ELIGIBILITY: 'Eligibility',
  DUPLICATE_CLAIM: 'Duplicate claim',
  TIMELY_FILING: 'Timely filing',
  COORDINATION_OF_BENEFITS: 'Other insurance',
  PATIENT_RESPONSIBILITY: 'Patient responsibility',
  UNDERPAYMENT: 'Underpayment',
  UNRECOGNIZED_DENIAL: 'Unrecognized denial',
  UNITS: 'Units',
  BENEFIT_LIMITS: 'Benefit limits',
  RECOUPMENT: 'Recoupment',
  PENDING_STATUS: 'Pending status',
  CLAIM_FORM: 'Claim form',
  COMPLIANCE_WITHDRAWAL: 'Withdraw unperformed service',
  MISSING_PAYER_CONTROL_NUMBER: 'Missing the original payer control number',
  MISSING_ORDERING_NPI: 'Missing a verified ordering provider NPI',
  contract_allowed: 'Allowed by the contract',
  already_paid: 'Already paid',
  not_performed: 'Not performed',
  denial_accepted: 'Denial accepted',
  advisory: 'Advisory',
  reconciled: 'Reconciled',
  unknown: 'Unknown',
};

const GATES = {
  'G-IDENTITY': 'Identity',
  'G-EVIDENCE': 'Evidence',
  'G-FINANCIAL': 'Payment',
  'G-APPROVAL': 'Approval',
  'G-TEMPORAL': 'Dates',
  'G-POLICY': 'Policy',
  'G-PERFORMED': 'Service performed',
  'G-CODING': 'Coding',
  'G-ACTION': 'Next action',
  'G-CLAIM-HISTORY': 'Claim history',
  'G-CONFLICT': 'Conflicts',
};

const RECOVERABLE = new Set(['RECONSIDERATION', 'APPEAL', 'CORRECT_CLAIM', 'CONTRACT_DISPUTE']);
const HOLDING = new Set(['HOLD_FOR_EVIDENCE', 'PROVIDE_INFORMATION', 'HUMAN_REVIEW', 'NOT_EVALUATED']);
const STOPPED = new Set(['VOID_OR_WITHDRAW_PROPOSAL', 'ACCEPT_DENIAL']);

const ACTIVE = new Set(['queued', 'running']);

function show(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  return String(value);
}

function when(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function stageState(id, log) {
  const events = (log || []).filter((item) => item.stage === id);
  const last = events[events.length - 1];
  if (!last) return 'pending';
  if (last.status === 'completed') return 'complete';
  if (last.status === 'failed') return 'failed';
  if (last.status === 'running') return 'running';
  return 'pending';
}

function stageFact(event) {
  if (!event) return 'Waiting to start.';
  const detail = event.detail || {};
  if (event.status === 'failed') return 'This step did not finish.';
  if (event.stage === 'snapshot') return 'The saved case was copied onto this run.';
  if (event.stage === 'references') return `${detail.reference_count ?? 0} reference sources were attached.`;
  if (event.stage === 'validate') return `Claim identity ${plain(detail.status)}.`;
  if (event.stage === 'inventory') return `${detail.count ?? 0} denial rows were listed.`;
  if (event.stage === 'activate') return `${detail.task_count ?? 0} checks were chosen.`;
  if (event.stage === 'execute') {
    return event.status === 'running'
      ? `Reading ${detail.task_count ?? 0} checks.`
      : `${detail.task_count ?? 0} checks finished.`;
  }
  if (event.stage === 'lines') return `${detail.line_count ?? 0} lines were decided.`;
  if (event.stage === 'conflicts') return detail.evidence_ok ? 'The important findings name a source.' : 'An important finding has no source.';
  if (event.stage === 'gates') return `${detail.gate_count ?? 0} checks were applied.`;
  if (event.stage === 'estimate') return 'The recovery estimate was recorded.';
  if (event.stage === 'remedies') return `${detail.warning_count ?? 0} notes. Nothing was filed.`;
  if (event.stage === 'revision') return detail.conclusion_changed ? 'The conclusion changed from the last run.' : 'The conclusion matches the last run.';
  return 'Recorded.';
}

function plain(value) {
  if (value === null || value === undefined || value === '') return '—';
  const text = String(value);
  if (WORDS[text]) return WORDS[text];
  if (/^[A-Z0-9_-]+$/.test(text)) {
    return text.toLowerCase().replace(/[_-]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
  return text;
}

function plainNote(text) {
  if (!text) return '';
  if (text.startsWith('Cited payer policy is not a verified live contract')) {
    return 'This policy is a test citation, not a live contract. Nothing has been filed.';
  }
  if (text.startsWith('UNKNOWN findings were not turned into an appeal')) {
    return 'A check did not have enough information, so it was not turned into an appeal.';
  }
  if (text.startsWith('Unregistered strategies stayed UNKNOWN')) {
    return 'A check is not in the catalog yet, so it was left undecided.';
  }
  if (text.startsWith('Activation limit reached')) {
    return 'Some checks were not run because the limit was reached.';
  }
  if (text === 'Resolution run completed.') return 'The review finished. Nothing has been filed.';
  if (text === 'Headless v1 does not submit actions.') return 'Nothing is filed until a person approves it.';
  if (text === 'Material findings cite a source.') return 'The important findings name a source.';
  if (text === 'Patient, claim, and plan identity match.') return 'The patient, claim, and plan match.';
  if (text.startsWith('The matching policy is an unverified candidate')) {
    return 'This policy matches the plan and the date of service. It is sample text, not a live contract, so the suggestion has not been sent to the payer.';
  }
  return text;
}

function dispositionTone(value) {
  if (RECOVERABLE.has(value)) return 'recover';
  if (HOLDING.has(value)) return 'hold';
  if (STOPPED.has(value)) return 'stop';
  return 'neutral';
}

function statusTone(value) {
  if (value === 'SUPPORTED' || value === 'PASS') return 'recover';
  if (value === 'NOT_SUPPORTED' || value === 'CONTRADICTED' || value === 'BLOCK') return 'stop';
  if (value === 'UNKNOWN' || value === 'REVIEW') return 'hold';
  return 'neutral';
}

function amountState(value) {
  if (value === null || value === undefined || value === '') return 'unknown';
  const number = Number(value);
  if (Number.isNaN(number)) return 'unknown';
  return number > 0 ? 'recover' : 'zero';
}

function Quote({ text, phrases }) {
  const value = text || '';
  const wanted = [...(phrases || [])].filter(Boolean).sort((left, right) => right.length - left.length);
  if (!value) return null;
  if (!wanted.length) return value;
  const pattern = new RegExp(wanted.map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'gi');
  const nodes = [];
  let last = 0;
  let index = 0;
  for (const match of value.matchAll(pattern)) {
    if (match.index > last) nodes.push(value.slice(last, match.index));
    nodes.push(<mark key={`${match.index}-${index}`}>{match[0]}</mark>);
    last = match.index + match[0].length;
    index += 1;
  }
  if (last < value.length) nodes.push(value.slice(last));
  return nodes;
}

function lineMeta(caseBundle, lineId) {
  const lines = caseBundle?.claim?.lines || [];
  return lines.find((item) => item.line_id === lineId) || {};
}

function serviceOf(source) {
  const procedure = source.procedure || {};
  const code = procedure.code || source.procedure_code || '';
  const description = procedure.description || source.procedure_description || source.description || '';
  const carcs = [...new Set((source.adjustments || []).map((item) => item.carc).filter(Boolean))];
  return { code, description, carcs };
}

function snapshotCitation(caseBundle, sourceId) {
  if (!sourceId) return null;
  const documents = caseBundle?.reference_documents?.documents || [];
  const document = documents.find((item) => item.source_id === sourceId);
  if (document) {
    const quotes = (document.extracted_facts || [])
      .map((item) => item.value)
      .filter(Boolean);
    const sectionPath = (document.extracted_facts || [])
      .map((item) => item.source_path)
      .find(Boolean) || '';
    return {
      kind: document.source_type === 'payer_policy' ? 'policy' : 'source',
      source_id: sourceId,
      label: document.source_type === 'payer_policy' ? 'Payer policy' : (document.title || 'Source'),
      quote: quotes.join(' ') || document.title || '',
      source_location: `reference_documents.documents[source_id=${sourceId}]`,
      matched: [],
      document_title: document.title || '',
      document_date: document.document_date || '',
      verification_status: document.verification_status || '',
      section_path: sectionPath,
    };
  }
  const clinical = caseBundle?.clinical_context || {};
  const order = (clinical.orders || []).find((item) => item.order_id === sourceId);
  if (order) {
    return {
      kind: 'chart',
      source_id: sourceId,
      label: 'Order',
      quote: (order.reason_for_order || []).join(' '),
      source_location: `clinical_context.orders[order_id=${sourceId}].reason_for_order`,
      matched: [],
      document_date: order.order_date || '',
      provider: order.ordering_provider || '',
    };
  }
  const report = (clinical.results || []).find((item) => item.report_id === sourceId);
  if (report) {
    return {
      kind: 'chart',
      source_id: sourceId,
      label: 'Report',
      quote: report.summary || '',
      source_location: `clinical_context.results[report_id=${sourceId}].summary`,
      matched: [],
      document_date: report.report_date || '',
      report_status: report.status || '',
    };
  }
  const encounter = clinical.encounter || {};
  if (encounter.encounter_id === sourceId) {
    return {
      kind: 'chart',
      source_id: sourceId,
      label: 'Chart note',
      quote: encounter.clinical_summary || '',
      source_location: 'clinical_context.encounter.clinical_summary',
      matched: [],
      document_date: encounter.encounter_date || encounter.date || '',
    };
  }
  return null;
}

function necessityCards(line, claimLine, caseBundle) {
  const finding = (line.findings || []).find((item) => item.strategy_id === 'MEDICAL_NECESSITY');
  if (!finding) return [];
  if ((finding.citations || []).length) return finding.citations;
  const cards = [];
  const seen = new Set();
  function add(card) {
    if (!card) return;
    const key = `${card.label}:${card.source_id}`;
    if (seen.has(key)) return;
    seen.add(key);
    cards.push(card);
  }
  (finding.supporting_source_ids || []).forEach((id) => add(snapshotCitation(caseBundle, id)));
  const clinical = caseBundle?.clinical_context || {};
  const encounter = clinical.encounter || {};
  if (encounter.clinical_summary) {
    add({
      kind: 'chart',
      source_id: encounter.encounter_id || 'encounter',
      label: 'Chart note',
      quote: encounter.clinical_summary,
      source_location: 'clinical_context.encounter.clinical_summary',
      matched: [],
    });
  }
  const orderIds = new Set(claimLine.order_ids || []);
  (clinical.orders || []).forEach((order) => {
    if (orderIds.size && !orderIds.has(order.order_id)) return;
    add(snapshotCitation(caseBundle, order.order_id));
  });
  const reportIds = new Set(claimLine.clinical_source_ids || []);
  (clinical.results || []).forEach((report) => {
    if (reportIds.size && !reportIds.has(report.report_id)) return;
    add(snapshotCitation(caseBundle, report.report_id));
  });
  return cards;
}

function MoneyFigure({ value, confirmed, compact }) {
  const state = amountState(value);
  const caption = state === 'recover'
    ? 'Can recover'
    : (state === 'zero' ? 'Nothing additional to collect' : 'Not estimated yet');
  return (
    <div className={`cb-money is-${state}${compact ? ' is-compact' : ''}`}>
      <span>{state === 'unknown' ? 'Estimate' : caption}</span>
      <strong>{state === 'unknown' ? 'Not estimated yet' : money(value)}</strong>
      {confirmed !== undefined ? <small>Confirmed payment {money(confirmed)}</small> : null}
    </div>
  );
}

export default function ClaimRunPage() {
  usePageTitle('Claim resolution');
  const { caseId, executionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const watch = Boolean(location.state?.watch);
  const [run, setRun] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(watch ? 0 : STAGES.length);

  useEffect(() => {
    setBusy(false);
    setRevealed(watch ? 0 : STAGES.length);
  }, [executionId, watch]);

  useEffect(() => {
    if (!watch || !run) return undefined;
    const recorded = STAGES.filter(([id]) => stageState(id, run.stage_log) !== 'pending').length;
    const goal = run.status === 'completed' ? STAGES.length : recorded;
    if (revealed >= goal) return undefined;
    const timer = setTimeout(() => setRevealed((count) => count + 1), 420);
    return () => clearTimeout(timer);
  }, [watch, run, revealed]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    setRun(null);
    setError('');

    async function pull() {
      try {
        const res = await dmClient.get(`/case-builder/cases/${caseId}/executions/${executionId}`);
        if (cancelled) return;
        const data = res.data.data;
        setRun(data);
        if (ACTIVE.has(data.status)) timer = setTimeout(pull, 500);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Could not open this run.');
      }
    }

    pull();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [caseId, executionId]);

  useEffect(() => {
    let cancelled = false;
    dmClient.get(`/case-builder/cases/${caseId}/executions`)
      .then((res) => {
        if (!cancelled) setHistory(res.data?.data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => { cancelled = true; };
  }, [caseId, run?.status]);

  async function runAgain() {
    setBusy(true);
    setError('');
    try {
      const res = await dmClient.post(`/case-builder/cases/${caseId}/executions`);
      navigate(`/case-builder/${caseId}/runs/${res.data.data.claim_resolution_execution_id}`, { state: { watch: true } });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start this run.');
      setBusy(false);
    }
  }

  const log = run?.stage_log || [];
  const recorded = STAGES.filter(([id]) => stageState(id, log) !== 'pending').length;
  const playback = watch && run && !(
    (run.status === 'completed' && revealed >= STAGES.length)
    || ((run.status === 'failed' || run.status === 'interrupted') && revealed >= recorded)
  );
  const focusIndex = Math.min(revealed, STAGES.length - 1);
  const focusEvent = [...log].reverse().find((item) => item.stage === STAGES[focusIndex][0]);
  const resolution = run?.case_resolution_bundle || {};
  const caseBundle = run?.case_bundle || {};
  const reference = run?.claim_reference_bundle || {};
  const done = run?.status === 'completed' && !playback;
  const financial = resolution.financial_outcome || {};
  const payer = caseBundle.insurance?.payer_name;
  const claim = caseBundle.claim?.claim_id;

  return (
    <div className="cb-page">
      <header className="cb-head">
        <div>
          <h1>Claim resolution</h1>
          <p>
            <Link to="/case-builder">Case Builder</Link>
            {' · '}
            <Link to={`/case-builder/${caseId}`}>{payer || claim || 'Case'}</Link>
            {' · '}
            <span className="cb-mono">{executionId}</span>
          </p>
        </div>
        <div className="cb-head-actions">
          {run?.status ? (
            <span className={`cb-badge is-${playback ? 'running' : run.status}`}>{playback ? 'running' : run.status}</span>
          ) : null}
          <button type="button" className="dm-btn dm-btn--secondary" onClick={runAgain} disabled={busy}>Run again</button>
        </div>
      </header>

      {error ? <p className="cb-error">{error}</p> : null}
      {!run && !error ? <p className="cb-note">Opening this run…</p> : null}

      {run ? (
        <div className="cb-run">
          <aside className="cb-rail">
            <section className="cb-panel">
              <h2>Stages</h2>
              <ol className="cb-stages">
                {STAGES.map(([id, label], index) => {
                  const actual = stageState(id, log);
                  let state = actual;
                  if (playback) {
                    if (index < revealed) state = actual === 'failed' ? 'failed' : 'complete';
                    else if (index === revealed) state = actual === 'failed' ? 'failed' : 'running';
                    else state = 'pending';
                  }
                  return (
                    <li key={id} className={`cb-stage is-${state}`}>
                      <i />
                      <div>
                        <strong>{label}</strong>
                        <span>{state}</span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
            <section className="cb-panel">
              <h2>Runs</h2>
              {history.length === 0 ? <p className="cb-note">No saved runs yet.</p> : (
                <ul className="cb-history">
                  {history.map((item) => (
                    <li key={item.claim_resolution_execution_id}>
                      <button
                        type="button"
                        className={item.claim_resolution_execution_id === executionId ? 'is-on' : ''}
                        onClick={() => navigate(`/case-builder/${caseId}/runs/${item.claim_resolution_execution_id}`)}
                      >
                        <span>{when(item.started_at)}</span>
                        <strong>{plain(item.final_disposition || item.status)}</strong>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </aside>

          <div className="cb-run-main">
            {!done ? (
              <section className="cb-panel">
                <h2>{run.status === 'failed' && !playback ? 'This run stopped' : STAGES[focusIndex][1]}</h2>
                <p>
                  {run.status === 'interrupted' && !playback
                    ? 'This run stopped before it finished.'
                    : (playback ? stageFact(focusEvent) : (run.error || stageFact(focusEvent)))}
                </p>
                <p className="cb-note">Step {focusIndex + 1} of {STAGES.length}</p>
              </section>
            ) : (
              <>
                <section className="cb-panel">
                  <div className="cb-outcome">
                    <div>
                      <h2>{plain(resolution.final_disposition)}</h2>
                      <p className="cb-note">{[payer, claim].filter(Boolean).join(' · ') || 'This claim'}</p>
                      {resolution.final_reason ? <p className="cb-reason">{plainNote(resolution.final_reason)}</p> : null}
                    </div>
                    <MoneyFigure
                      value={financial.estimated_additional_collectible}
                      confirmed={financial.confirmed_additional_paid}
                    />
                  </div>
                  <dl className="cb-facts">
                    <div><dt>Payer</dt><dd>{show(payer)}</dd></div>
                    <div><dt>Claim</dt><dd>{show(claim)}</dd></div>
                    <div><dt>Review status</dt><dd>{plain(resolution.analysis_status)}</dd></div>
                    <div><dt>Next step</dt><dd>{plain(resolution.execution_status)}</dd></div>
                    <div><dt>Started</dt><dd>{when(run.started_at)}</dd></div>
                  </dl>
                </section>

                {(resolution.line_resolutions || []).map((line) => {
                  const source = lineMeta(caseBundle, line.line_id);
                  const service = serviceOf(source);
                  const cards = necessityCards(line, source, caseBundle);
                  const citedIds = new Set(cards.map((item) => item.source_id));
                  const otherFindings = (line.findings || []).filter((item) => (item.strategy_id !== 'MEDICAL_NECESSITY' || cards.length === 0) && item.status !== 'UNKNOWN');
                  const otherEvidence = (line.evidence_chain || []).filter((item) => !citedIds.has(item.source_id));
                  return (
                    <section className="cb-panel cb-line" key={line.line_id}>
                      <header className="cb-line-head">
                        <div>
                          <h2>{service.code || `Line ${line.line_id}`}{service.description ? ` · ${service.description}` : ''}</h2>
                          <p className="cb-note">
                            Line {line.line_id}
                            {service.carcs.length ? ` · Denial code ${service.carcs.join(', ')}` : ''}
                          </p>
                        </div>
                        <span className={`cb-badge is-${dispositionTone(line.disposition)}`}>{plain(line.disposition)}</span>
                      </header>
                      <div className="cb-line-body">
                        <div>
                          <p className="cb-reason">{line.reasoning_summary}</p>
                          <dl className="cb-facts">
                            <div><dt>How the amount was priced</dt><dd>{line.estimate_basis && line.estimate_basis !== 'not_calculated' ? plain(line.estimate_basis) : '—'}</dd></div>
                            <div><dt>Confidence</dt><dd>{plain(line.confidence)}</dd></div>
                          </dl>
                        </div>
                        <MoneyFigure value={line.estimated_additional_collectible} compact />
                      </div>

                      {cards.length ? (
                        <>
                          <h3>Supporting evidence</h3>
                          <ul className="cb-cites">
                            {cards.map((item) => {
                              const docName = item.document_title || item.label || 'Source';
                              const where = [
                                item.section_path ? item.section_path.replace(/sections\[(\d+)\]/g, (_, n) => `Section ${Number(n) + 1}`) : '',
                                item.document_date || '',
                              ].filter(Boolean).join(' · ');
                              return (
                              <li key={`${item.label}-${item.source_id}`} className={`cb-cite is-${item.kind === 'policy' ? 'policy' : 'chart'}`}>
                                <div className="cb-cite__doc">
                                  <span className="cb-cite__icon">{item.kind === 'policy' ? '📋' : '🏥'}</span>
                                  <div>
                                    <strong>{docName}</strong>
                                    {where ? <span className="cb-cite__where">{where}</span> : null}
                                  </div>
                                </div>
                                <blockquote className="cb-cite__quote"><Quote text={item.quote} phrases={item.matched} /></blockquote>
                              </li>
                              );
                            })}
                          </ul>
                        </>
                      ) : null}

                      {(line.corrections || []).length ? (
                        <>
                          <h3>Recommended corrections</h3>
                          <ul className="cb-corrections">
                            {line.corrections.map((c, i) => (
                              <li key={`corr-${i}`} className="cb-correction">
                                {c.action === 'add_diagnosis' ? (
                                  <div className="cb-correction__row">
                                    <span className="cb-correction__icon">➕</span>
                                    <div>
                                      <strong>Add {c.icd10_code}</strong> — {c.description}
                                      {c.diagnosis_pointer ? <span className="cb-id"> (pointer {c.diagnosis_pointer})</span> : null}
                                      <p className="cb-note">{c.rationale}</p>
                                    </div>
                                  </div>
                                ) : c.action === 'confirm_pointer' ? (
                                  <div className="cb-correction__row">
                                    <span className="cb-correction__icon">✓</span>
                                    <div>
                                      <strong>{c.icd10_code}</strong> — {c.description}
                                      {c.diagnosis_pointer ? <span className="cb-id"> (pointer {c.diagnosis_pointer})</span> : null}
                                      <p className="cb-note">{c.rationale}</p>
                                    </div>
                                  </div>
                                ) : c.action === 'reorder_primary' ? (
                                  <div className="cb-correction__row">
                                    <span className="cb-correction__icon">↕</span>
                                    <div>
                                      <strong>Review primary diagnosis</strong>
                                      <p className="cb-note">{c.rationale}</p>
                                      <p className="cb-note">Current primary: {c.current_primary}. Options: {(c.recommended_primary_options || []).join(', ')}</p>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="cb-correction__row">
                                    <span className="cb-correction__icon">📝</span>
                                    <div><strong>{c.action}</strong><p className="cb-note">{c.rationale || c.field}</p></div>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : null}

                      <h3>Other checks</h3>
                      {otherFindings.length === 0 ? <p className="cb-note">No other check on this line.</p> : (
                        <ul className="cb-findings">
                          {otherFindings.map((finding) => (
                            <li key={finding.finding_id || `${finding.strategy_id}-${finding.status}`}>
                              <div className="cb-check-row">
                                <div>
                                  <strong>{plain(finding.strategy_id)}</strong>
                                  <div className="cb-id">{finding.strategy_id}</div>
                                </div>
                                <span className={`cb-badge is-${statusTone(finding.status)}`}>{plain(finding.status)}</span>
                              </div>
                              {finding.statement ? <p>{finding.statement}</p> : null}
                            </li>
                          ))}
                        </ul>
                      )}

                      {otherEvidence.length ? (
                        <>
                          <h3>Other sources</h3>
                          <ul className="cb-cites">
                            {otherEvidence.map((item) => {
                              const looked = snapshotCitation(caseBundle, item.source_id);
                              const docName = looked?.document_title || looked?.label || item.source_id;
                              return (
                                <li key={item.evidence_id || item.source_id} className="cb-cite is-chart">
                                  <div className="cb-cite__doc">
                                    <span className="cb-cite__icon">🏥</span>
                                    <strong>{docName}</strong>
                                  </div>
                                  <blockquote className="cb-cite__quote">{looked?.quote || item.quote_or_fact || item.supports}</blockquote>
                                </li>
                              );
                            })}
                          </ul>
                        </>
                      ) : null}

                      {(line.alternatives_considered || []).length ? (
                        <>
                          <h3>Considered and not selected</h3>
                          <ul className="cb-gaps">
                            {line.alternatives_considered.map((item) => (
                              <li key={item.proposal}>{plain(item.proposal)}{item.reason ? ` — ${item.reason}` : ''}</li>
                            ))}
                          </ul>
                        </>
                      ) : null}

                      {(line.blocked_dependencies || []).length ? (
                        <>
                          <h3>Waiting on</h3>
                          <ul className="cb-gaps">
                            {line.blocked_dependencies.map((item) => (
                              <li key={item.warning_code}>{plain(item.warning_code)} <span className="cb-mono">{item.reference}</span></li>
                            ))}
                          </ul>
                        </>
                      ) : null}

                      {(line.conflicts || []).length ? (
                        <>
                          <h3>Conflicts</h3>
                          <ul className="cb-gaps">
                            {line.conflicts.map((item) => (
                              <li key={item.conflict_id}>{item.question} {item.resolved ? '(resolved)' : '(still open)'}</li>
                            ))}
                          </ul>
                        </>
                      ) : null}
                    </section>
                  );
                })}

                <section className="cb-panel">
                  <h2>Checks</h2>
                  <ul className="cb-gates">
                    {(resolution.claim_level_gates || []).map((gate) => (
                      <li key={gate.gate_id}>
                        <span className={`cb-badge is-${statusTone(gate.status)}`}>{plain(gate.status)}</span>
                        <div>
                          <strong>{GATES[gate.gate_id] || plain(gate.gate_id)}</strong>
                          <div className="cb-id">{gate.gate_id}</div>
                          {gate.reason ? <p>{plainNote(gate.reason)}</p> : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>

                {(resolution.warnings || []).length || (resolution.unresolved_issues || []).length ? (
                  <section className="cb-panel">
                    <h2>Open items</h2>
                    <ul className="cb-gaps">
                      {(resolution.warnings || []).map((item) => <li key={item}>{plainNote(item)}</li>)}
                      {(resolution.unresolved_issues || []).map((item) => <li key={item}>{plainNote(item)}</li>)}
                    </ul>
                  </section>
                ) : null}

                <section className="cb-panel">
                  <h2>References on this run</h2>
                  <div className="cb-table-wrap">
                    <table className="cb-table">
                      <thead>
                        <tr><th>Type</th><th>Id</th><th>Graph</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {(reference.reference_sets || []).length === 0 ? (
                          <tr><td colSpan={4}>No reference sources on this run.</td></tr>
                        ) : (reference.reference_sets || []).map((item, index) => (
                          <tr key={`${item.reference_type}-${item.reference_id}-${index}`}>
                            <td>{show(item.reference_type)}</td>
                            <td>{show(item.reference_id)}</td>
                            <td className="cb-mono">{show(item.graph_id)}</td>
                            <td>{plain(item.applicability_status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
